/*  MailerCopy - shows a printed (USPS) letter in the message center as real 8.5 x 11 sheets.

    The API's mailer logs each mailed letter to z_email_log (delivery_method 'usps') with its
    HTML wrapped in <div class="mailer-copy">. That HTML is one continuous .page built for
    paper, so here it is cut back into sheets the way the printer would:
      - a new sheet at every element whose CSS says break-before: page (.page-break)
      - a new sheet wherever the content runs past the sheet's printable height
      - a table that runs over is split by rows, with its header repeated
      - a block marked break-after: avoid (.section-heading) moves with the block after it
    Then the stack is shown at the letter's true width, scaled down only when the pane is
    narrower than 8.5in.

    The letter is moved into a shadow root first. The app's own stylesheets are page-wide -
    the Dashboard's `.totals { display:flex }` turned the letter's totals row into a flex box,
    and Bootstrap's box-sizing, p and table defaults all reach in too - so without this the
    preview drifts from the printed PDF. Inside the shadow root only the letter's CSS applies.

    Same file in the CRM (client/src/mvc/components/mailerCopy) and the Dashboard
    (assets/shared) - keep the two in step.

    Usage, after the message HTML is in the DOM:   MailerCopy.layout(containerElement)
    The container gets the class "is-mail" while it holds a mailed letter, so each app can
    drop its email-card framing for it. Anything that is not a mailer copy is left alone.
*/
// Leading ';' - this file is concatenated into compiled.js after a file that ends in `})`
// with no semicolon, which would otherwise call that expression with this function.
;(function (window) {
  var SHEET_W   = 816     // 8.5in at 96px/in
  var SHEET_H   = 1056    // 11in
  var MARGIN    = 48      // 0.5in - the letter's @page bottom margin, and the top margin of a continued sheet
  var USABLE_H  = SHEET_H - MARGIN
  var SHEET_GAP = 24

  var current = null      // last laid-out container, re-fitted on resize

  function layout(container, tries) {
    if (!container) return
    var copy = container.querySelector('.mailer-copy')
    container.classList.toggle('is-mail', !!copy)
    if (!copy) { current = null; return }
    current = container

    if (!copy.getAttribute('data-paged')) {
      var root = isolate(copy)
      var page = root.querySelector('.page')
      if (!page) return
      // Still hidden measures as 0. The CRM hides its preview while a message loads, and with
      // ngAnimate the ng-show that reveals it lands a frame or two later - so wait for it
      // (up to ~1s) rather than lay out blind. Past that, the plain copy stays.
      if (!page.offsetHeight) {
        tries = tries || 0
        if (tries < 60) requestAnimationFrame(function () { layout(container, tries + 1) })
        return
      }
      paginate(copy, root, page)
      copy.setAttribute('data-paged', '1')
    }
    fit(container)
  }

  // Moves the letter (its <style> and .page) into a shadow root on the .mailer-copy element,
  // under an inner .mailer-copy so the stored copy's scoped selectors still match.
  // Returns that inner element - or the copy itself where shadow DOM is unavailable.
  function isolate(copy) {
    if (copy.shadowRoot) return copy.shadowRoot.querySelector('.mailer-copy')
    if (!copy.attachShadow) return copy
    var shadow = copy.attachShadow({ mode: 'open' })
    var reset = document.createElement('style')
    // Inherited properties (font, color, line-height ...) still cross the boundary - start from initial.
    reset.textContent = ':host { all: initial; display: block; }'
    var inner = document.createElement('div')
    inner.className = 'mailer-copy'
    while (copy.firstChild) inner.appendChild(copy.firstChild)
    shadow.appendChild(reset)
    shadow.appendChild(inner)
    return inner
  }

  function paginate(copy, root, page) {
    var content = page.querySelector('.letter-content')
    var flowSource = content || page
    var lead = []       // blocks above the flowing content - the address area - first sheet only
    var trail = []
    if (content) {
      var before = true
      Array.prototype.slice.call(page.children).forEach(function (child) {
        if (child === content) { before = false; return }
        (before ? lead : trail).push(child)
      })
    }
    var blocks = Array.prototype.slice.call(flowSource.children)

    var host = document.createElement('div')
    host.className = 'mailer-sheets'
    host.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:' + SHEET_GAP + 'px;padding:4px 0 12px;'
    root.insertBefore(host, page)
    copy.style.overflowX = 'visible'
    copy.style.background = 'transparent'

    var sheet = null   // { page, flow }

    function newSheet(first, continued) {
      var el = document.createElement('div')
      el.className = 'mailer-sheet'
      el.style.cssText = 'width:' + SHEET_W + 'px;min-height:' + SHEET_H + 'px;background:#ffffff;box-sizing:border-box;' +
        'position:relative;box-shadow:0 1px 3px rgba(16,24,40,.12),0 8px 24px rgba(16,24,40,.10);'
      var p = page.cloneNode(false)
      p.style.minHeight = '0'
      el.appendChild(p)
      var flow = p
      if (content) {
        if (first) lead.forEach(function (n) { p.appendChild(n) })
        flow = content.cloneNode(false)
        p.appendChild(flow)
      }
      // A sheet started by overflow needs its own top margin; one started by an explicit
      // page break gets it from that block's own padding.
      if (continued) flow.style.paddingTop = (parseFloat(getComputedStyle(flow).paddingTop) + MARGIN) + 'px'
      host.appendChild(el)
      sheet = { page: p, flow: flow }
      return sheet
    }

    function over() { return sheet.page.offsetHeight > USABLE_H }

    function breaksBefore(el) {
      var cs = getComputedStyle(el)
      return cs.breakBefore === 'page' || cs.pageBreakBefore === 'always'
    }

    function keepsWithNext(el) {
      var cs = getComputedStyle(el)
      return cs.breakAfter === 'avoid' || cs.pageBreakAfter === 'avoid'
    }

    // Moves block (plus any keep-with-next block just above it) onto a fresh sheet.
    function carryOver(block) {
      var carried = [block]
      var prev = block.previousElementSibling
      if (prev && keepsWithNext(prev) && sheet.flow.children.length > 2) carried.unshift(prev)
      newSheet(false, true)
      carried.forEach(function (n) { sheet.flow.appendChild(n) })
    }

    function splitTable(table) {
      var body = table.tBodies[0]
      if (!body || body.rows.length < 2) return
      var moved = []
      while (over() && body.rows.length > 1) {
        moved.unshift(body.rows[body.rows.length - 1])
        body.removeChild(body.rows[body.rows.length - 1])
      }
      if (over()) {
        // Not even one row fits under what is already on this sheet - start the table on the next.
        moved.forEach(function (r) { body.appendChild(r) })
        if (sheet.flow.children.length > 1) { carryOver(table); if (over()) splitTable(table) }
        return
      }
      if (!moved.length) return
      var rest = table.cloneNode(false)
      if (table.tHead) rest.appendChild(table.tHead.cloneNode(true))
      var restBody = body.cloneNode(false)
      moved.forEach(function (r) { restBody.appendChild(r) })
      rest.appendChild(restBody)
      newSheet(false, true)
      sheet.flow.appendChild(rest)
      if (over()) splitTable(rest)
    }

    newSheet(true, false)
    blocks.forEach(function (block) {
      if (sheet.flow.children.length && breaksBefore(block)) newSheet(false, false)
      sheet.flow.appendChild(block)
      if (!over()) return
      if (block.tagName === 'TABLE') splitTable(block)
      else if (sheet.flow.children.length > 1) carryOver(block)
      // a single block taller than a sheet stays where it is - the sheet just grows
    })
    trail.forEach(function (n) { sheet.page.appendChild(n) })

    page.parentNode.removeChild(page)
  }

  // True width when the pane has room for it; otherwise the whole stack scales down.
  function fit(container) {
    var copy = container.querySelector('.mailer-copy')
    var host = copy && (copy.shadowRoot || copy).querySelector('.mailer-sheets')
    if (!host) return
    host.style.zoom = 1
    var room = host.parentNode.clientWidth    // .mailer-copy - the content width left after the app's own padding
    host.style.zoom = room && room < SHEET_W ? (room / SHEET_W).toFixed(4) : 1
  }

  var resizeTimer = null
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(function () { if (current && document.body.contains(current)) fit(current) }, 120)
  })

  window.MailerCopy = { layout: layout, fit: fit }
})(window)
